/**
 * AttachmentListModal — iPad Settings style attachment viewer.
 *
 * Shows existing attachments in a rounded-2xl card with rows + dividers.
 * Upload button at the bottom opens the AttachmentPickerDialog.
 * Delete on hover per row.
 *
 * Designed to be triggered from the header paperclip button.
 */

import { useState, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Upload, MinusCircle, FileText, Image as ImageIcon, File } from "lucide-react";
import { useAttachments, useDeleteAttachment } from "@/lib/attachments/hooks";
import { AttachmentPickerDialog } from "@/components/attachments";
import { formatFileSize } from "@/lib/attachments/utils";
import type { AttachmentContext, Attachment } from "@/lib/attachments/types";
import { useLanguage } from "@/i18n/useLanguage";
import { getUITranslation } from "@/lib/views/labels/translations";

interface AttachmentListModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  doctype: string;
  name: string;
  /** Fallback attachments from sidebar data (used before hook loads) */
  fallbackAttachments?: { name: string; file_name: string; file_url: string; creation: string }[];
}

function FileTypeIcon({ mimeType, fileName }: { mimeType?: string | null; fileName: string }) {
  const ext = fileName.split(".").pop()?.toLowerCase();
  const isImage = mimeType?.startsWith("image/") || ["jpg", "jpeg", "png", "gif", "webp", "svg"].includes(ext ?? "");
  const isPdf = mimeType === "application/pdf" || ext === "pdf";

  if (isImage) return <ImageIcon className="h-5 w-5 text-muted-foreground/70" />;
  if (isPdf) return <FileText className="h-5 w-5 text-muted-foreground/70" />;
  return <File className="h-5 w-5 text-muted-foreground/70" />;
}

export function AttachmentListModal({
  open,
  onOpenChange,
  doctype,
  name,
  fallbackAttachments,
}: AttachmentListModalProps) {
  const { language } = useLanguage();
  const queryClient = useQueryClient();
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const uiLabels = useMemo(() => {
    const getUI = (key: string, fallback: string) => getUITranslation(key, language) || fallback;
    return {
      attachments: getUI("attachments", "Attachments"),
      deleteConfirm: getUI("delete_attachment_confirm", "Delete this attachment?"),
      uploadFiles: getUI("upload_files", "Upload files"),
      done: getUI("done", "Done"),
      noAttachments: getUI("no_attachments", "No attachments yet"),
      filesAttached: getUI("files_attached", "files attached to this document"),
      edit: getUI("edit", "Edit"),
    };
  }, [language]);

  const context: AttachmentContext = { doctype, name };
  const { data: attachmentsData } = useAttachments(context, { enabled: !!name && open });
  const deleteMutation = useDeleteAttachment(context);

  const attachments = useMemo<Attachment[]>(() => {
    if (attachmentsData) return attachmentsData.attachments ?? [];
    if (fallbackAttachments?.length) {
      return fallbackAttachments.map((att) => ({
        docname: att.name,
        file_name: att.file_name,
        file_url: att.file_url,
        mime_type: undefined as string | undefined,
        file_size: undefined as number | undefined,
        is_private: 0,
        attached_to_field: undefined as string | undefined,
        creation: att.creation,
      })) as Attachment[];
    }
    return [];
  }, [attachmentsData, fallbackAttachments]);

  const handleDelete = async (attachment: Attachment) => {
    if (!confirm(uiLabels.deleteConfirm)) return;
    try {
      await deleteMutation.mutateAsync(
        attachment.docname
          ? { file_docname: attachment.docname }
          : { file_url: attachment.file_url }
      );
      queryClient.invalidateQueries({ queryKey: ["doc-sidebar", doctype, name] });
    } catch {
      // Error handled by mutation hook
    }
  };

  const handleUploadCompleted = () => {
    queryClient.invalidateQueries({ queryKey: ["doc-sidebar", doctype, name] });
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => { if (!v) setEditMode(false); onOpenChange(v); }}>
        <DialogContent className="max-h-[85vh] max-w-[680px] p-0 flex flex-col overflow-hidden">
          {/* Top fade gradient */}
          <div className="absolute top-0 left-0 right-0 z-30 h-12 bg-gradient-to-b from-white via-white/95 to-transparent dark:from-gray-900 dark:via-gray-900/95 pointer-events-none will-change-[opacity]" />

          {/* Scrollable content */}
          <div className="flex-1 overflow-y-auto relative will-change-[scroll-position]">
            <DialogHeader className="px-10 pt-20 pb-4 !flex-row flex items-center justify-between space-y-0">
              <div>
                <DialogTitle className="text-[22px] font-semibold text-foreground leading-tight tracking-[-0.2px]">
                  {uiLabels.attachments}
                </DialogTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  {attachments.length} {uiLabels.filesAttached}
                </p>
              </div>
              {attachments.length > 0 && (
                <button
                  type="button"
                  onClick={() => setEditMode((prev) => !prev)}
                  className="text-[17px] font-normal text-[#007AFF] hover:text-[#0051D5] active:text-[#0040B3] transition-colors shrink-0"
                >
                  {editMode ? uiLabels.done : uiLabels.edit}
                </button>
              )}
            </DialogHeader>

            {/* File list */}
            <div className="px-10 pb-8 pt-2">
              {attachments.length === 0 ? (
                <div className="flex flex-col items-center py-10">
                  <File className="h-12 w-12 text-muted-foreground/40 mb-3" />
                  <p className="text-base font-medium text-foreground">{uiLabels.noAttachments}</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Upload files to attach them to this document
                  </p>
                </div>
              ) : (
                <div className="bg-gray-50 dark:bg-gray-950 rounded-md overflow-hidden border border-gray-200/40 dark:border-gray-700/40">
                  {attachments.map((attachment, i) => (
                    <div key={attachment.docname || attachment.file_url}>
                      <div className="flex items-center gap-3 px-5 py-4">
                        {/* Edit mode: red minus circle slides in from left */}
                        <div
                          className="shrink-0 overflow-hidden transition-all duration-200 ease-in-out"
                          style={{ width: editMode ? 28 : 0, opacity: editMode ? 1 : 0 }}
                        >
                          <button
                            type="button"
                            onClick={() => handleDelete(attachment)}
                            disabled={deleteMutation.isPending}
                            className="flex items-center justify-center text-destructive disabled:opacity-50"
                            aria-label={`Delete ${attachment.file_name}`}
                          >
                            <MinusCircle className="h-[22px] w-[22px]" />
                          </button>
                        </div>

                        <div className="flex items-center justify-center h-10 w-10 rounded-md bg-muted/50 shrink-0">
                          <FileTypeIcon mimeType={attachment.mime_type} fileName={attachment.file_name} />
                        </div>
                        <a
                          href={attachment.file_url}
                          target="_blank"
                          rel="noreferrer"
                          className="flex-1 min-w-0"
                        >
                          <p className="text-[17px] text-gray-900 dark:text-gray-100 truncate hover:text-primary transition-colors">
                            {attachment.file_name}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {attachment.file_size ? formatFileSize(attachment.file_size) : "\u2014"}
                          </p>
                        </a>
                      </div>
                      {i < attachments.length - 1 && <div className="mx-5 border-t border-gray-200/40 dark:border-gray-700/40" />}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Bottom fade gradient */}
            <div className="sticky bottom-0 left-0 right-0 z-10 h-24 bg-gradient-to-t from-white via-white/95 to-transparent dark:from-gray-900 dark:via-gray-900/95 pointer-events-none will-change-[opacity]" />
          </div>

          {/* Sticky footer — outside scroll container */}
          <div className="flex gap-3 px-10 py-6 bg-white dark:bg-gray-900 border-t border-gray-200/50 dark:border-gray-800/50 flex-shrink-0">
            <Button
              variant="outline"
              className="flex-1 rounded-full h-11 gap-2"
              onClick={() => setShowUploadDialog(true)}
            >
              <Upload className="h-4 w-4" />
              {uiLabels.uploadFiles}
            </Button>
            <Button
              className="rounded-full h-11 bg-[#007AFF] hover:bg-[#0051D5] active:bg-[#0040B3] text-white"
              onClick={() => onOpenChange(false)}
            >
              {uiLabels.done}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload sub-dialog */}
      <AttachmentPickerDialog
        open={showUploadDialog}
        onOpenChange={setShowUploadDialog}
        context={context}
        multiple
        onCompleted={handleUploadCompleted}
      />
    </>
  );
}
